import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { ExtendedTheme } from "@insite/mobius/ThemeProvider";
import get from "@insite/mobius/utilities/get";
import getProp from "@insite/mobius/utilities/getProp";
import styled, { createGlobalStyle, GlobalStyleComponent } from "styled-components";

export interface GlobalStyleProps { }

const fontSize = () => ({ theme }: { theme: BaseTheme }) => {
    const size = get(theme, "typography.body.size", 15);
    return typeof size === "string" ? size : `${size}px`;
};

/**
 * GlobalStyle is used to set the basic CSS for the site.
 * Use it only for style rules that apply to the entire site.
 * Since this creates styling at a global level,
 * place it in the outermost element of the site to avoid multiple instantiation.
 *
 * Out of the box body behavior - removed to acomplish custom design
 *
 */
const GlobalStyle = createGlobalStyle<GlobalStyleProps>`
    html {
        font-family: ${getProp("theme.typography.body.fontFamily")};
        box-sizing: border-box;
        height: 100%;
        font-size: 100%;
    }
    :root {
        font-size: 100%;
    }
    ::selection {
        color: #fff;
        background: #005CB9;
    }
    body[aria-hidden="true"] {
        overflow: hidden !important;
    }
    body {
        margin: 0;
        line-height: 1.5;
        font-size: ${getProp("theme.typography.body.size")};
        font-weight: ${getProp("theme.typography.body.weight")};
        color: ${getProp("theme.colors.text.main")};   
        background: #FFF;
        overflow:auto !important;
        min-height: 100%;
        position: relative;
    }
    body[aria-hidden="true"] {
        overflow: hidden !important;
    }
    header,main {
        background-color:  #FFF; !important;
    }   
    
    ul {
        margin: 0;
        padding: 0;
        list-style: none;
    }
    a {
        color: inherit;
        cursor: pointer; // for links without href
        text-decoration: none;
    }

    /* Typography resets */
    span, p, h1, h2, h3, h4, h5, h6 {
        margin: 0;
        padding: 0;
        font-size: inherit;
        font-weight: inherit;
    }
    *, *::before, *::after {
        box-sizing: inherit;
    }
    #react-app {
        height: 100%;
    }
    .justifyBetween {
        justify-content: space-between;
        display: flex;
        width: 100%;
    }
    .list-wrapper {
        label[data-test-selector="paginationPerPageSelect-label"] {
            color: #333F48;
            font-family: Roboto;
            font-size: .875rem;
            letter-spacing: .01rem;
            font-weight: normal;
        }
    }    

    .p-relative {
        position: relative;
    }

    label.hide-this-element {
        top: -9999px;
        position: absolute;
    }

    div[data-test-selector="page_ProductListPage"] { 
        nav[data-test-selector="pageBreadcrumbs"] {            
            a:nth-child(3) {
                span[class*='TypographyStyle'] {                    
                    text-transform:capitalize;                    
                }
            }
        }
        .qty-container {
            label[for="orderedQtyInput"] {
                top: -9999px;
                position: absolute;
                visibility: hidden;
            }
            button {
                top: .35rem !important;
            }

            .input-container {
                div > div {
                    margin-top: 0;

                    input {
                        input[data-test-selector="product_qtyOrdered"]{
                            margin-top: 0;
                        }
                    }
                }
            }
            @media screen and (max-width: 1300px) { 
                width: 9.8rem !important;
                max-width: 9.8rem !important;
            }
        }

        .adjust-spacing {
            .stock-container {
                .request-link {
                    a {
                        span {
                            font-size: .9rem;
                            color:#333F48 !important;
                            text-decoration: underline;
                            font-weight: 700 !important;
                            
                            &:hover {
                                color:#2060AD !important;
                            }
                        }
                    }
                }
            }
        }
    }

    .pagination-arrow {
        li button {
            &[data-test-selector*="currentPageNumber"] {
                span {
                    font-weight: bold !important;
                }
            }
            &[data-test-selector*="currentPageNumber"]{
                color: #535E64 !important;
                font-family: 'Roboto',sans-serif;
                
                &:visited {
                    color: #002554 !important; 
                    text-decoration: underline;
                    font-family: 'Roboto',sans-serif;
                    span {
                        font-weight: bold !important;
                    }
                } 
                &:active {
                    color: #535E64 !important;
                }
            }
            &:not([data-test-selector*="currentPageNumber"]){
                position: relative;
                &:visited,
                &:hover {
                    font-family: 'Roboto',sans-serif;

                    span[class*='TypographyStyle'],
                    span[class^='TypographyStyle'] {
                        position: relative;
                        text-shadow: 0 0 1px #2060AD;

                        &:after {
                            content: "";
                            border-bottom: 2px solid #2060AD;
                            position: absolute;
                            bottom: 0;
                            width: 100%;
                            display: block;
                        }
                    }
                }
                &:visited {
                    color: #002554 !important;
                } 
                &:hover {
                    color: #2060AD !important;
                }
                &:disabled {
                    color: #9C9C9C !important;
                } 
            }
        }

        li:first-child,
        li:last-child {
            button {
                position: relative;
                top: .02em;
                border-color: transparent !important;
                &:not(:disabled) {
                    &:hover {
                        span[class*='IconWrapper'],
                        span[class^='IconWrapper'] {
                            position: relative;

                            svg {
                                path {
                                    stroke: #2060AD;
                                }
                            }

                            &:after {
                                content: "";
                                border-bottom: 2px solid #2060AD;
                                position: absolute;
                                bottom: 0;
                                width: 100%;
                                display: block;
                            } 
                        }
                    }
                }

                span[class*='IconWrapper'],
                span[class^='IconWrapper'] {
                    position: relative;
                    svg {
                        height: .75rem;
                        width: .75rem;
                        color: #000;
    
                        path {
                            fill: #000;
                        }
                    }
                }          
            }

            button:disabled {
                color: #9C9C9C;

                span[class*='IconWrapper'],
                span[class^='IconWrapper'] {
                    border-bottom: 2px solid #9C9C9C; 

                    svg {
                        fill: #9C9C9C;
                        color: #9C9C9C;
                        height: .8125rem;
                        width: .75rem;
                        stroke: #9C9C9C;
    
                        path {
                            fill: #9C9C9C;
                        }
                    }
                }
            }
        }

        li:nth-child(2),
        li:nth-last-child(2) {
            button {
                border-color: transparent !important;
                &:not(:disabled) {
                    &:hover {
                        span[class*='IconWrapper'],
                        span[class^='IconWrapper'] {
                            position: relative;

                            svg {
                                path {
                                    stroke: #2060AD;
                                }
                            }

                            &:after {
                                content: "";
                                border-bottom: 2px solid #2060AD;
                                position: absolute;
                                bottom: 0;
                                width: 100%;
                                display: block;
                            }
                        }
                    }
                }
                
                span[class*='IconWrapper'],
                span[class^='IconWrapper'] {
                    position: relative;
                    svg {
                        height: .719rem;
                        color: #000;
    
                        path {
                            fill: #000;
                        }
                    }
                }
            }

            button:disabled {
                span[class*='IconWrapper'],
                span[class^='IconWrapper'] {
                    border-bottom: 2px solid #9C9C9C;
                    
                    svg {
                        color: #9C9C9C;
                        stroke: #9C9C9C;
    
                        path {
                            fill: #9C9C9C;
                        }
                    }
                } 
            }
        }
    }

    .filter-wrapper,
    div[data-test-selector="productListFilterDrawer"]{
        div[data-test-selector="stockedItemsOnlyFilter"] {
            button {
                &:hover {
                    svg {
                        height: 1.3125rem;
                        position: relative;
                        top: -.125rem;
                    }
                }
            }
        }
        dt {
            button {
                border: none !important;
                background: transparent !important;
                padding: .250625rem .0625rem !important;

                span {
                    color: #002554;
                    font-family: Roboto;
                    font-size: .8125rem;
                    font-weight: 500;
                    letter-spacing: .009rem;
                    line-height: 1.3125rem;
                }

                svg {
                    height: 1.25rem;
                    width: 1.25rem
                }
            }

            button[aria-expanded="true"] {
                span[role="presentation"] {
                    position: relative;
                    right: .313rem;
                }
            }
        }
        dd {
            padding: 0 .0625rem;
            border: none !important;
            
            legend {
                margin-bottom: 0 !important;
            }

            span[role="checkbox"] {
                margin-top: .0625rem;
                min-width: .938rem;
                border: 1px solid #1B5EAF;
                border-radius: 0;

                svg {
                    height: .9375rem;
                    width: .938rem;
                }

                &:focus {
                    box-shadow: 0 0 0 1px #002554;
                }
            }

            label {
                color: #002554;
                font-family: Lato;
                font-size: .875rem;
                font-weight: bold;
                letter-spacing: .01rem;
                line-height: 1.125rem;
                word-break: break-word;
                -webkit-hyphens: auto;
                -ms-hyphens: auto;
                hyphens: auto;
                -webkit-hyphenate-limit-before: 1;
                -webkit-hyphenate-limit-after: 1;
                -ms-hyphenate-limit-chars: auto;
                hyphenate-limit-chars: 10 1 1;
                -moz-hyphenate-limit-chars: 10 1 1;
            }
        }
        
        label[for="productsKeywordSearch"], 
        h6 {
            color: #002554;
            font-size: .8125rem;
            font-weight: 500;
            letter-spacing: .009rem;
            line-height: .875rem;
            margin-bottom: .75rem;
            margin-top: 0;
            display: inline-block;
        }

        input[data-test-selector="productListSearchWithin"] {
            border: 1px solid #e0e7ff !important;
            border-radius: 2px !important;
            background-color: #F9FAFF !important;
            color: #002554 !important;
            font-size: .875rem;
            letter-spacing: .01rem;
            line-height: 1.3125rem;
        }
    }

    div[data-test-selector="page_ProductDetailsPage"] {
        @media screen and (min-width: 1200px) {
            header + div{
                min-height: 3.3125rem;
            } 
        }
        @media screen and (min-width: 768px) {
            header + div{
                min-height: 4.6875rem;
            } 
        }
        @media screen and (max-width: 767px) {
            header + div{
                min-height: 6.125rem;
            } 
        }               

        nav[data-test-selector="pageBreadcrumbs"] {            
            a:nth-child(3) {
                span[class*='TypographyStyle'] {                    
                    text-transform:capitalize;                    
                }
            }
        }
        .input-container {
            div > div {
                margin-top: 0 !important;

                input {
                    margin-top: .43750625rem !important;
                }
            }
            button {
                top: 1.9rem !important;
            }
        }
        
        .mainProductDetails {
            .product-carousel {
                @media screen and (min-width: 768px) {
                    padding: 2rem 2.1rem 3.45rem 1rem;
                    max-width: 100%;
                    margin: 0;

                    .slide-header {
                        margin-left: 0;
                    }
                }
            }
        }
    }

    @media screen and (min-width: 1200px) {
        .table-row {
            &:nth-child(odd),
            &:nth-child(even) {
                background-color: #fff !important;
            }
        }
    }
    @media screen and (min-width: 992px) {
        div[data-test-selector="page_ProductListPage"] {
            .qty-container {
                margin-top: 0 !important;
                margin-bottom: .31250625rem !important;

                label[for="orderedQtyInput"] {
                    font-family: Lato;
                    font-size: .875rem;
                    font-style: italic;
                    letter-spacing: 0;
                    line-height: 1.5rem;
                }
            }
        }
    }
    @media screen and (min-width: 768px) {
        .card_container__wrapper{
            &:nth-child(odd),
            &:nth-child(even) {
                background-color: #fff !important;
                box-shadow: 10em 0 0 0 #fff, -10em 0 0 0 #fff;
            }
       }
    }
    @media screen and (max-width: 767px) {
       .card_container__wrapper{
            &:nth-child(even) {
               background-color: #f7f9fa;
               box-shadow: 10em 0 0 0 #f7f9fa, -10em 0 0 0 #f7f9fa;
            }
            &:nth-child(odd) {
                background-color: #fff;
                box-shadow: 10em 0 0 0 #fff, -10em 0 0 0 #fff;
            }
       }
       
        div[data-test-selector="page_ProductListPage"] {
            main {
                padding-top: 0;
            }

            // header {        
            //     overflow: hidden;
            // }

            .qty-container {
                margin-top: 0 !important;
                margin-bottom: .31250625rem !important;

                label[for="orderedQtyInput"] {
                    font-family: Lato;
                    font-size: .875rem;
                    font-style: italic;
                    letter-spacing: 0;
                    line-height: 1.5rem;
                }
            }
       }
    }

    @media screen and (max-width: 79.99em) {
        .mobile-view {
            display: block;
        }
        .desktop-view {
            display: none;
        }

        .pagination-arrow {
            li {
                text-align: center;
            }
            li:first-child{
                text-align: left;
            }
            li:last-child {
                text-align: right;
            }
        }

        .filter-wrapper {
            display: block;
            width: 100%;
            flex-basis: 100%;
            max-width: 100%;
            box-shadow: 10em 0 0 0 #fff, -10em 0 0 0 #fff;
        }
        .list-wrapper {
            margin-top:-.625rem;
            padding-top: 0;
            @media screen and (min-width: 1400px) {
                .list-wrapper {
                        flex-basis: 80% !important;
                        max-width: 80%;
                        padding: .625rem .625rem .625rem 1.7rem;
                }
    
            }
            @media screen and (min-width: 1280px) and (max-width: 1399px) {
                .list-wrapper {
                    flex-basis: 78%;
                        max-width: 78%;
                }
    
            }

            > div {
                .grid-control{
                    position: relative;
                    
                    &:last-child {
                        margin-top: .625rem;
                        position: relative;

                        .control-last {
                            display: none;
                        }
                        .control-first {
                            width: 50%;
                            flex-basis: 50%;
                            max-width: 50%;
                            font-style: normal;
                            font-weight: normal;
                        }
                        .control-second {
                            div > span {
                                width: 50%;
                                flex-basis: 50%;
                                max-width: 50%;
                                position: absolute;
                                left: 50%;
                                top: 0;

                                label {
                                    width: auto;
                                }
                            }
                            div > nav {
                                margin-top: 1.25rem;
                            }
                            span > div > div {
                                max-width: initial;
                                width: initial;
                                display: inline-block;
                                vertical-align: top;
                            }
                        }
                    }
                }
            }
        }
       

        div[data-test-selector="page_ProductListPage"] {
            .adjust-spacing {
                margin-top: -.31250625rem;
                margin-bottom: .2em;

                span[data-test-selector="availabilityMessage"]:not(.req-lead-time) {
                    font-size: .875rem !important;
                    font-family: 'Roboto', sans-serif;
                    color: #002554;
                    font-weight: bold !important;
                    letter-spacing: .028rem;

                    span {
                        font-size: .875rem !important;
                        font-family: 'Roboto', sans-serif;
                        color: #002554 !important;
                        letter-spacing: .028rem;
                    }
                }

                .req-lead-time {
                    font-size: .875rem !important;
                    font-weight: 400 !important;
                    text-decoration: underline;
                    span {
                        font-size: .9rem;
                        color:#333F48 !important;
                        text-decoration: underline;
                        font-weight: 700 !important;
                        &:hover {
                            color:#2060AD !important;
                        }
                    }
                }
                .stock-container {
                    font-size: .875rem !important;
                    font-family: 'Roboto', sans-serif;
                    color: #002554;
                    font-weight: bold !important;
                    letter-spacing: .028rem;

                    span:first-child {
                        font-size: .875rem !important;
                        font-family: 'Roboto', sans-serif;
                        color: #002554 !important;
                        font-weight: bold !important;
                        letter-spacing: .028rem;
                    }
                }
            }
            > div {
                header + div {
                    background-color: #F7F9FA !important;
                }
            }
            main {
                background-color: #F7F9FA !important;
                padding-top: 0;
                padding-bottom: 1.875rem;
                overflow: hidden;
            }
            footer {
                > div {
                    > div {
                        margin-top: 0;
                    }
                }
            }
            .price-section {
                margin-bottom: 0 !important;
                position: relative;
            }
            button[data-test-selector="quantityBreakPricingLink"] {
                max-width: 5.9375rem;
                word-break: break-all;
                white-space: normal;
                display: block;
            }
            .last-grid-item {
                display: none; 
            }

            nav[data-test-selector="pageBreadcrumbs"] {
                padding-left: 0 !important;
            }
        }

        .filter-wrapper,
        div[data-test-selector="productListFilterDrawer"]{
            .filter-wrapper-head {
                + div {
                    display: none;
                }
            }
            .acc-wrapper {
                max-width: 50%;
                display: inline-block;
                width: 50%; 
                box-sizing: border-box;
                margin-right: 0;
                vertical-align: top;
                position: relative;

                &:nth-child(odd) {
                    padding-right: .625rem;
                }

                &.Brands,
                &.brand {
                    display: inline-block !important; 
                }

                &.Filter.By,
                &.Stocked,
                &.stocked {
                    dd {
                        position: static;
                    }

                    dt {
                        display: none;
                    }

                    div[role="group"] {
                        padding-left: 0;
                        border-bottom: 1px solid #C2D1D9 !important;
                        margin-bottom: 0;
                    }

                    dd {
                        padding-top: .125rem;
                        padding-bottom: 0;

                        span[role="checkbox"] {
                            position: absolute;
                            top: .313rem;
                        }

                        label {
                            color: #333F48;
                            font-family: Lato;
                            font-size: .875rem;
                            font-weight: bold;
                            letter-spacing: 0;
                            line-height: 1.25rem;
                            margin-left: 1.875rem;
                        }
                    }
                }

                &:nth-of-type(1n+2) {
                    display: none;
                }

                dd {
                    div[role="group"] {
                        border-bottom: 1px solid #C2D1D9;
                        padding: .31250625rem !important;
                        line-height: 1;
                        margin-bottom: .31250625rem;
                        display: inline-block;
    
                        &:last-child {
                            border-bottom: none;
                        }
                    }
                }
            }

            dd {
                padding-top: .625rem;
                padding-bottom: .625rem;

                &:hidden {
                    display: none;
                    padding: .625rem;
                    background: #fff;
                }

                span[role="checkbox"] {
                    min-width: 1.25rem;
                    height: 1.25rem;
                    border: 1px solid #C2D1D9;
                    opacity: .93;
                    border-radius: .125rem;
                    
                    svg {
                        height: 1.25rem;
                        width: 1.25rem;
                    }
                }

                label {
                    color: #333F48;
                    font-family: Roboto;
                    font-size: .875rem;
                    letter-spacing: 0;
                    line-height: 1.25rem;
                }
            }
            dt {
                border-bottom: 1px solid #C2D1D9;

                button {
                    svg {
                        height: 1rem;
                        width: 1rem;
                    }

                    span {
                        color: #333F48;
                        font-family: Lato;
                        font-size: .875rem;
                        font-weight: bold;
                        letter-spacing: 0;
                        line-height: 1.25rem;
                    }
                }
            }
            
            .acc-wrapper.Brands dd,
            .acc-wrapper.brands dd {
                position: absolute;
                z-index: 9;
                background: #fff;
                box-shadow: 0 6px 24px 0 rgb(0 0 0 / 18%);
                padding: .625rem .31250625rem;
                width: 98%;
            }

            &.brand-page {
                position: relative;
                .Categories,
                .categories {
                    position: initial;
                    order: 2;
                    display: inline-block !important; 

                    dt {
                        width: 100%;
                    }

                    dd {
                        position: absolute;
                        z-index: 9;
                        background: #fff;
                        box-shadow: 0 6px 24px 0 rgb(0 0 0 / 18%);
                        padding: .625rem .31250625rem;
                        width: 97.4%;
                        left: 10px;
                        max-height: 900px;
                        overflow: auto;
                    }
                }
            }
       }

       .input-container {
            button {
                top: 2.1rem !important;
            }
           > div {
                > div {
                    margin-top: 0 !important;
                }
           }
       }
       .show-price-link {
            margin: .250625rem 0;
            display: inline-block;
            width: auto;
            position: absolute;
            right: .938rem;
            text-align: right;
            color: #002554;
            font-family: Roboto;
            font-size: .875rem;
            letter-spacing: 0;
            line-height: 1rem;
            text-decoration: underline;

            span {
                color: #002554 !important;
                font-family: Roboto;
                font-size: .875rem;
            }
       }

       div[data-test-selector="page_ProductListPage"] {
        div.qty-container {
            button {
                top: .5rem !important;
            }
        }
    }
    }
    @media screen and (min-width: 992px) and (max-width: 1399px) {
        div[data-test-selector="page_ProductListPage"] {
            nav[data-test-selector="pageBreadcrumbs"] {
                padding-left: .6rem !important;
            }
        }
       
    }
    @media screen and (min-width: 1440px) {
        .price-subheading {
            display: none;
        }
    }
    @media screen and (min-width: 80em) {

        .mobile-view {
            display: none;
        }
        .desktop-view {
            display: block;
        }
        .filter-wrapper,
        div[data-test-selector="productListFilterDrawer"]{
            dl > div {
                display: flex;
                flex-direction: column;
            }
            .acc-wrapper {
                width: 100%;
            }
        }
        .filter-wrapper {
            box-shadow: 0px 5px #fff;

            .acc-wrapper {
                &.Filter.By,
                &.Brands,
                &.brands,
                &.Stocked,
                &.stocked {
                    dt {
                        button {
                            span[role="presentation"] {
                                display: none !important;
                            }
                        }
                    }
                    dd {
                        display: block !important;
                    }
                }
            }

            // &.brand-page {
            //     .Categories,
            //     .categories {
            //         dd {
            //             display: block !important;
            //         }
            //     }
            // }
        }

        div[data-test-selector="page_ProductListPage"] {
            .adjust-spacing{
                .req-lead-time {
                    font-size: .9rem !important;
                    text-decoration: underline;
                    span {
                        font-size: .9rem;
                        color:#333F48 !important;
                        text-decoration: underline;
                        font-weight: 700 !important;
                        &:hover {
                            color:#2060AD !important;
                        }
                    }
                    
                }
                .stock-container {
                    span:first-child {
                        color: #002554 !important;
                    }
                }
            }

            button[data-test-selector="quantityBreakPricingLink"] {
                max-width: 5.9375rem;
                word-break: break-all;
                white-space: normal;
                display: block;
            }
            > div {
                header + div {
                    background-color: #F7F9FA !important;
                }
            }
            
            nav[data-test-selector="pageBreadcrumbs"] {
                margin-left: 18rem;
                padding-left: 0 !important;
                margin-top: .31250625rem !important;
            }
            main {
                padding: 0 .625rem 1.875rem .625rem;
                background-color: #F7F9FA !important;
                width: 100%; 
                height: auto;
                div > h1 {
                        margin-left: 18.3rem;
                        padding-left: 0;
                    }
                }
            }
            footer {
                > div {
                    > div {
                        margin-top: 0;
                    }
                }
            }
        }
    }
    
    div[data-test-selector="page_MyAccountPage"] {
        background: #f7f9fa;
        > div {
            header + div {
                background-color: #f7f9fa !important;
            }
        }

        h1[data-test-selector="PageTitle"] {
            position: relative;

            &:after {
                height: .19rem;
                width: 2.5rem;
                background-color: rgb(198, 53, 39);
                content: "";
                display: inline-block;
                position: absolute;
                bottom: -1rem;
                left: 1px;
            }
            > div {
               display: none;
            }
        }

        @media (max-width: 1199px) {
            > div {
                max-width: 100%;
            } 
        }

        main[data-test-selector="myAccount"] {
            background: #f7f9fa !important; 

            @media (max-width: 1199px) {
                max-width: 100% !important;

                div[class^='GridWrapper'], div[class*=' GridWrapper']{
                    max-width: 100% !important;
                }
            }

            @media (max-width: 990px) {
                overflow: hidden;
            }
        }

        nav[data-test-selector="pageBreadcrumbs"] {
            padding-left: 0;
        }

        @media screen and (min-width: 768px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                margin-left: 17.625rem;
            }

            h1[data-test-selector="PageTitle"] {
                margin-left: 0;
                margin-bottom: 15px;
                font-weight: 500;
            }
        }

        @media screen and (min-width: 990px) {
            h1[data-test-selector="PageTitle"] {
                margin-left: .625rem;
            }
        }

        @media screen and (max-width: 990px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                display: none;
            }
            > div {
                header + div {
                    display: none;
                }
            }
        }

        @media screen and (max-width: 1279px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                display: none;
            } 
            > div {
                header + div {
                    display: none;
                }
            }
            
        }
    }

    div[data-test-selector="page_AddressesPage"] {
        background: #f7f9fa;
        > div {
            header + div {
                background-color: #f7f9fa !important;
            }
        }

        main[data-test-selector="addressPage"] {
            background: #f7f9fa !important;
            @media (max-width: 1199px) {
                max-width: 100% !important;

                div[class^='GridWrapper'], div[class*=' GridWrapper']{
                    max-width: 100% !important;
                }
            }

            @media (max-width: 990px) {
                overflow: hidden;
            }

            h1[data-test-selector="PageTitle"] {
                font-size: 22px;
                font-weight: 500;
                letter-spacing: 0;
            }
        }

        nav[data-test-selector="pageBreadcrumbs"] {
            padding-left: 0;
        }

        @media screen and (min-width: 768px){
            nav[data-test-selector="pageBreadcrumbs"] {
                margin-left: 17.625rem;
            }
        }

        @media screen and (max-width: 1279px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                display: none;
            } 
            > div {
                header + div {
                    display: none;
                }
            }
        }
       
    }

    .confirm-remove-modal {
        button[data-test-selector="modalCloseButton"] {
            display: none;
        }
        div#modalTitle {
            padding: 0;
            border: none;
        }
    }

    div[data-test-selector="page_CartPage"] {
        > div {
            header + div {
                background-color: #f7f9fa !important;
            }
        }
        main {
            background: #F7F9FA !important;
            overflow: hidden;
        }
        
        nav[data-test-selector="pageBreadcrumbs"] {
            padding-left: 0;

            @media print {
                display: none;
            }
        }

        .card_container__wrapper {
            margin-bottom: 15px;
        }

        @media screen and (min-width: 990px) {
            nav[data-test-selector="pageBreadcrumbs"],
            .cart-header-container {
                padding-left: 10px;
            }
        }
    }
   
    div[data-test-selector="page_HomePage"] main, div[data-test-selector="page_ProductListPage"] main, div[data-test-selector="page_ProductDetailsPage"] main, div[data-test-selector="page_CategoryDetailsPage"] main, div[data-test-selector="page_page_BrandsPage"] main, div[data-test-selector="page_Page"] main, div[data-test-selector="page_CartPage"] main, div[data-test-selector="page_CartPage"] > div, div[data-test-selector="page_CheckoutReviewAndSubmitPage"] main, div[data-test-selector="page_CheckoutReviewAndSubmitPage"] > div {
        @media (min-width: 576px) and (max-width: 1199px) {
            max-width: 100% !important;

        }
    }
    div[data-test-selector="page_CheckoutShippingPage"] {
        div[class^='GridOffset'], div[class*=' GridOffset'] {
            width: calc(100% + 15px) !important;
        }
        main[data-test-selector="checkoutShipping"] {
            .side-wrapper {
                width: 100%;

                .grid-container {
                    > div {
                        div:nth-child(odd) {
                            > span {
                                color: #7C878E;
                                font-family: Roboto, sans-serif;
                                font-size: 16px;
                                font-weight: 500;
                                letter-spacing: -0.31px;
                                line-height: 23px;

                                &.total-value {
                                    opacity: 0.7;
                                    color: #C63527;
                                    font-family: Roboto;
                                    font-size: 22px;
                                    font-weight: bold;
                                    letter-spacing: -0.42px;
                                    line-height: 23px;
                                    text-align: right;
                                }
                            }
                            > span[data-test-selector="cartTotal_subTotal"] {
                                font-weight: bold;
                            }
                            .total-label {
                                color: #333F48;
                                font-family: Roboto, sans-serif;
                                font-size: 20px;
                                font-weight: 500;
                                letter-spacing: -0.38px;
                                line-height: 23px;
                                text-align: left !important;
                            }
                        }
                        div:nth-child(even) {
                            text-align: right;
                            > span {
                                color: #333F48;
                                font-family: Roboto;
                                font-size: 20px;
                                letter-spacing: -0.38px;
                                line-height: 23px;
                            }
                            > span[data-test-selector="cartTotal_subTotal"] {
                                font-weight: bold;
                            }
                        }
                    }
                }
            }
        }
    }
    div[data-test-selector="page_CheckoutReviewAndSubmitPage"] {
        background: #f7f9fa;
        
        nav[data-test-selector="pageBreadcrumbs"]
        {
            display: none;
        }
        main[data-test-selector="checkoutReviewAndSubmitPage"] {
            background: #f7f9fa !important;

            @media (min-width: 991px) {
                overflow: hidden;
            }

            @media (max-width: 1199px) {
                max-width: 100% !important;
    
                div[class^='GridWrapper'], div[class*=' GridWrapper']{
                    max-width: 100% !important;
                }

                .checkout-total-container {
                    div[class^='GridOffset'],
                    div[class*=' GridOffset'] {
                        width: calc(100% + 10px);
                    }
                }
            }

            @media (max-width: 990px) {
                padding-bottom: 50px;
            }
        }
    }
    div[data-test-selector="page_OrderConfirmationPage"] {
        background: #f7f9fa;
        
        nav[data-test-selector="pageBreadcrumbs"] {
            display: none;
        }
        
        .confirm-main {
            max-width: 62rem;
            background-color: transparent !important;

            @media print {
                max-width: 99%;
                .confirmation-header,
                .confirmation-footer {
                    button {
                        display: none !important;
                    }
                }
            }
        }

        @media screen and (max-width: 768px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                padding-left: 0;
            }
        }

        > div {
            // max-width: 62.0625rem;

            @media print {
                header, footer {
                    display: none !important;
                }
            }
        }
    }
    div[data-test-selector="page_ProductListPage"] nav[data-test-selector="pageBreadcrumbs"], div[data-test-selector="page_ProductDetailsPage"] nav[data-test-selector="pageBreadcrumbs"], div[data-test-selector="page_CategoryDetailsPage"] nav[data-test-selector="pageBreadcrumbs"], div[data-test-selector="page_Page"] nav[data-test-selector="pageBreadcrumbs"], div[data-test-selector="page_CreateAccountPage"] nav[data-test-selector="pageBreadcrumbs"],  div[data-test-selector="page_BrandsPage"] nav[data-test-selector="pageBreadcrumbs"], div[data-test-selector="page_CartPage"] nav[data-test-selector="pageBreadcrumbs"] {
        @media screen and (max-width: 991px) {
            padding-left: 0;
            }
        }
    }

    
    div[data-test-selector="confirmRemoveAllModal"] #modalTitle {
        display: none;
    }

    div[data-test-selector="page_MyListsDetailsPage"] {
        div[data-test-selector="myListDetails_line"]:last-child {
            margin-bottom: 15px;
        }
    }
    
    div[data-test-selector="page_OrderHistoryPage"] {
        background: #f7f9fa;

        > div {
            header + div {
                background-color: #f7f9fa !important;
            }
        }
        main[data-test-selector="orderHistory"] {
            background: #f7f9fa !important;
        }

        @media screen and (max-width: 1279px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                display: none;
            } 
            > div {
                header + div {
                    display: none;
                }
            }
        }

        h1[data-test-selector="PageTitle"] {
            position: relative;
            margin-bottom: 2.5rem;
            font-weight: 500;
        }
        h1[data-test-selector="PageTitle"] svg {
            display: none;
        }
        
        h1[data-test-selector="PageTitle"]:after {
            content: '';
            display: block;
            width: 40px;
            height: 3px;
            background: #C63527;
            position: relative;
            bottom: -15px;
        }

        @media screen and (max-width: 767px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                padding-left: 0;
            }
        }
    }

    div[data-test-selector="page_SavedOrderListPage"] {
        background: #f7f9fa;

        > div{
            header + div {
                background-color: #f7f9fa !important;
            }
        }
        main {
            background: #f7f9fa !important;
            overflow: initial;
        }

        @media screen and (max-width: 1279px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                display: none;
            } 
            > div {
                header + div {
                    display: none;
                }
            }
        }

        h1[data-test-selector="PageTitle"] {
            position: relative;
            margin-bottom: 1.5625rem;
            font-weight: 500;
        }

        h1[data-test-selector="PageTitle"] svg {
            display: none;
        }
        
        h1[data-test-selector="PageTitle"]:after {
            content: '';
            display: block;
            width: 40px;
            height: 3px;
            background: #C63527;
            position: relative;
            bottom: -15px;
        }

        @media screen and (max-width: 767px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                padding-left: 0;
            }
        }

        @media screen and (min-width: 768px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                margin-left: 17.625rem;
                padding-left: 0;
            }

            h1[data-test-selector="PageTitle"] {
                padding-left: 0;
            }
        }
        
        @media (max-width: 1199px) {
            main {
                max-width: 100% !important;

                div[class^='GridWrapper'], div[class*=' GridWrapper']{
                    max-width: 100% !important;
                }
            }
        }
    }

    div[data-test-selector="page_OrderHistoryPage"] {
        background: #f7f9fa;
        > div {
            background: #f7f9fa !important;
        }

        @media (max-width: 1199px) {
            main[data-test-selector="orderHistory"] {
                max-width: 100%;
                overflow: initial;

                > div {
                    max-width: 100%;
                }
            }
            > div {
                max-width: 100%;
            } 
        }

        main[data-test-selector="orderHistory"] {
            background: #f7f9fa !important;

            @media (max-width: 1199px) {
                max-width: 100% !important;

                div[class^='GridWrapper'], div[class*=' GridWrapper']{
                    max-width: 100% !important;
                }
            }
        }

        @media screen and (min-width: 768px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                margin-left: 17.625rem;
                padding-left: 0;
            }

            h1[data-test-selector="PageTitle"] {
                padding-left: 0;
            }
        }

        @media screen and (max-width: 990px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                display: none;
            }
            > div {
                header + div {
                    display: none;
                }
            }
        }

        @media (max-width: 990px) {
            overflow: hidden;
        }
    }
    
    div[data-test-selector="page_ProductDetailsPage"] main{
        @media (max-width: 1199px) {
            max-width: 100% !important;            

            div[class^='GridWrapper'], div[class*='GridWrapper']{
                max-width: 100% !important;
            }
        }
    }
    
    div[data-test-selector="page_OrderDetailsPage"] {
        > div {
            header + div {
                background-color: #f7f9fa !important;
            }
        }
        main {
            background-color: #f7f9fa !important;
        }

        h1[data-test-selector="PageTitle"] svg {
            display: none;
        }
        
        h1[data-test-selector="PageTitle"]:after {
            content: '';
            display: block;
            width: 40px;
            height: 3px;
            background: #C63527;
            position: relative;
            bottom: -15px;
        }        

        @media screen and (max-width: 1279px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                display: none;
            }
            > div {
                header + div {
                    display: none;
                }
            }
        }
        
        @media (max-width: 1199px) {
            > main {
                max-width: 100% !important;
            }        

            div[class^='GridWrapper'], div[class*='GridWrapper']{
                max-width: 100% !important;
            }
        }        

        @media screen and (min-width: 768px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                margin-left: 17.625rem;
                padding-left: 0;
            }
        }

        @media screen and (max-width: 767px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                padding-left: 0;
            }
        }
        
    }

    div[data-test-selector="page_RfqQuoteDetailsPage"] {
        > div {
            header + div {
                background-color: #f7f9fa !important;
            }
        }
        
         main {
            background-color: #f7f9fa !important;
        }

        h1[data-test-selector="PageTitle"] svg {
            display: none;
        }
        
        h1[data-test-selector="PageTitle"]:after {
            content: '';
            display: block;
            width: 40px;
            height: 3px;
            background: #C63527;
            position: relative;
            bottom: -15px;
        }        

        @media screen and (max-width: 1279px) {
            nav[data-test-selector="pageBreadcrumbs"], 
            > div {
                header + div {
                    display: none;
                }
            }
        }
        
        @media (max-width: 1199px) {
            > main {
                max-width: 100% !important;
            }        

            div[class^='GridWrapper'], div[class*='GridWrapper']{
                max-width: 100% !important;
            }
        }        

        @media screen and (min-width: 768px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                margin-left: 17.625rem;
                padding-left: 0;
            }
        }

        @media screen and (max-width: 767px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                padding-left: 0;
            }
        }
        
    }
    div[data-test-selector="page_RfqQuoteDetailPage"] {
        > div {
            header + div {
                background-color: #f7f9fa !important;
            }
        }
        
         main {
            background-color: #f7f9fa !important;
        }

        h1[data-test-selector="PageTitle"] svg {
            display: none;
        }
        
        h1[data-test-selector="PageTitle"]:after {
            content: '';
            display: block;
            width: 40px;
            height: 3px;
            background: #C63527;
            position: relative;
            bottom: -15px;
        }        

        @media screen and (max-width: 1279px) {
            nav[data-test-selector="pageBreadcrumbs"], 
            > div {
                header + div {
                    display: none;
                }
            }
        }
        
        @media (max-width: 1199px) {
            > main {
                max-width: 100% !important;
            }        

            div[class^='GridWrapper'], div[class*='GridWrapper']{
                max-width: 100% !important;
            }
        }        

        @media screen and (min-width: 768px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                margin-left: 17.625rem;
                padding-left: 0;
            }
        }

        @media screen and (max-width: 767px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                padding-left: 0;
            }
        }
        
    }

    .not-homepage {
        .prod-items-wrapper {
            > div:first-child a {
                padding-top: 0.5rem;
            }
        }
    }

    div[data-test-selector="page_HomePage"] {
        @media screen and (min-width: 990px) {
            .prod-items-wrapper {
                > div:first-child {
                    a {
                        padding-top: 1.625rem;
                    }
                }
            }
            .nav-style-wrapper {
                &:before, 
                &:after {
                    content: none;
                    width: 0;
                    height: 0;
                    border-left: 18px solid transparent;
                    border-right: 18px solid transparent;
                    border-top: 18px solid #f0f2f5;
                    position: absolute;
                    bottom: -18px;
                    z-index: 3;
                    left: 26px;
                }
                &:after {
                    border-top-color: #c2d1d9;
                    bottom: -19px;
                    z-index: 2;
                }
            }
        }
    }
    
    div[data-test-selector="page_OrderStatusPage"] {
        > div {
            header + div {
                background-color: #f7f9fa !important;
            }
        }
        > main {
            background-color: #f7f9fa !important;
        }

        h1[data-test-selector="PageTitle"] svg {
            display: none;
        }
        
        h1[data-test-selector="PageTitle"]:after {
            content: '';
            display: block;
            width: 40px;
            height: 3px;
            background: #C63527;
            position: relative;
            bottom: -15px;
        }

        @media screen and (max-width: 1279px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                display: none;
            } 
            > div {
                header + div {
                    display: none;
                }
            }
        }
        
        @media (max-width: 1199px) {
            > main {
                max-width: 100% !important;
            }        

            div[class^='GridWrapper'], div[class*='GridWrapper']{
                max-width: 100% !important;
            }
        }

        @media screen and (max-width: 767px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                padding-left: 0;
            }
        }
    }

    div[data-test-selector="page_RfqQuoteDetailPage"] {
        > div {
            header + div {
                background-color: #f7f9fa !important;
            }
        }
        
         main {
            background-color: #f7f9fa !important;
        }

        h1[data-test-selector="PageTitle"] svg {
            display: none;
        }
        
        h1[data-test-selector="PageTitle"]:after {
            content: '';
            display: block;
            width: 40px;
            height: 3px;
            background: #C63527;
            position: relative;
            bottom: -15px;
        }        

        @media screen and (max-width: 1279px) {
            nav[data-test-selector="pageBreadcrumbs"], 
            > div {
                header + div {
                    display: none;
                }
            }
        }
        
        @media (max-width: 1199px) {
            > main {
                max-width: 100% !important;
            }        

            div[class^='GridWrapper'], div[class*='GridWrapper']{
                max-width: 100% !important;
            }
        }        

        @media screen and (min-width: 768px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                margin-left: 17.625rem;
                padding-left: 0;
            }
        }

        @media screen and (max-width: 767px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                padding-left: 0;
            }
        }
        
    }
    
    div[data-test-selector="page_RfqQuotePage"] {
        > div {
            header + div {
                background-color: #f7f9fa !important;
            }
        }
        
        main {
            background-color: #f7f9fa !important;
        }

        h1[data-test-selector="PageTitle"] svg {
            display: none;
        }
        
        h1[data-test-selector="PageTitle"]:after {
            content: '';
            display: block;
            width: 40px;
            height: 3px;
            background: #C63527;
            position: relative;
            bottom: -15px;
        }        

        @media screen and (max-width: 1279px) {
            nav[data-test-selector="pageBreadcrumbs"], 
            > div {
                header + div {
                    display: none;
                }
            }
        }
        
        @media (max-width: 1199px) {
            > main {
                max-width: 100% !important;
            }        

            div[class^='GridWrapper'], div[class*='GridWrapper']{
                max-width: 100% !important;
            }
        }        

        @media screen and (min-width: 768px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                margin-left: 17.625rem;
                padding-left: 0;
            }
        }

        @media screen and (max-width: 767px) {
            nav[data-test-selector="pageBreadcrumbs"] {
                padding-left: 0;
            }
        }
        
    .hidden-element {
        display: none;
    }

    @media print {
        footer, button {
            display: none !important;
        }
        h1 {
            font-size: .9375rem !important;
        }
        a {
            color: ${getProp("theme.colors.text.main")} !important;
        }
        body {
            font-size: .6875rem !important;
        }
        div[data-test-selector="page_OrderConfirmationPage"] {
            background-color: #fff !important;
        }
    }

    ${getProp("theme.globalStyle.css")};
` as GlobalStyleComponent<GlobalStyleProps, ExtendedTheme>;

export const ChildGlobals = styled.div`
    font-family: ${getProp("theme.typography.body.fontFamily")};
    color: ${getProp("theme.colors.text.main")};
    font-size: ${fontSize()};
`;

/** @component */
export default GlobalStyle;